import { all, call, put, select, takeLatest } from 'redux-saga/effects';

import type { AccessibleFleet, Session, User } from '@cobliteam/common-types';

import { type UserAttributes, UserRequester } from '@cobliteam/api-lib';

import { AlertTypes, showAlertActionCreator } from '@cobliteam/alert';
import { Environment } from '@cobliteam/react-environments';

import type { AppState } from '@cobliteam/store';
import {
  deleteUserRequestFailed,
  deleteUserRequestSucceeded,
  loadAccessibleFleetsSucceeded,
  loadUserAttributesFailed,
  loadUserAttributesSucceeded,
  loadUsersRequestFailed,
  loadUsersRequestSucceeded,
} from '@cobliteam/store/src/Users/actionCreators';
import type { DeleteUserRequest } from '@cobliteam/store/src/Users/actions';
import { UsersActionTypes } from '@cobliteam/store/src/Users/actionTypes';

const env = Environment.getInstance().getEnvironment();

export class UsersSagas {
  private userRequester: UserRequester;

  constructor(userRequester: UserRequester) {
    this.userRequester = userRequester;
    this.requestLoadFleetUsersSaga = this.requestLoadFleetUsersSaga.bind(this);
    this.requestDeleteUserSaga = this.requestDeleteUserSaga.bind(this);
    this.requestLoadUserAttributesSaga =
      this.requestLoadUserAttributesSaga.bind(this);
    this.requestLoadAcessibleFleets =
      this.requestLoadAcessibleFleets.bind(this);
  }

  public *requestLoadFleetUsersSaga() {
    try {
      const session: Session = yield select(
        (state: AppState) => state.login.session
      );

      if (session) {
        const fleetId = session.fleet.id;
        const fleetUsers: User[] = yield call(
          this.userRequester.getFleetUsers,
          fleetId
        );
        yield put(loadUsersRequestSucceeded(fleetUsers));
      }
    } catch (err) {
      yield put(loadUsersRequestFailed());
      yield put(
        showAlertActionCreator(
          AlertTypes.ERROR,
          'Houve um erro, tente novamente mais tarde.'
        )
      );
    }
  }

  public *requestDeleteUserSaga(action: DeleteUserRequest) {
    try {
      const session: Session = yield select(
        (state: AppState) => state.login.session
      );

      if (session) {
        const fleetId = session.fleet.id;

        yield call(this.userRequester.deleteFleetUser, fleetId, action.payload);
        yield put(deleteUserRequestSucceeded());
        yield put(
          showAlertActionCreator(
            AlertTypes.SUCCESS,
            'Usuário excluído com sucesso!'
          )
        );
      }
    } catch (err) {
      yield put(deleteUserRequestFailed());
      yield put(
        showAlertActionCreator(
          AlertTypes.ERROR,
          'Houve um erro, tente novamente mais tarde.'
        )
      );
    }
  }

  public *requestLoadUserAttributesSaga() {
    try {
      const userAttributes: UserAttributes = yield call(
        this.userRequester.getUserAttributes
      );
      yield put(loadUserAttributesSucceeded(userAttributes));
    } catch (err) {
      yield put(
        showAlertActionCreator(
          AlertTypes.ERROR,
          'Houve um erro, tente novamente mais tarde.'
        )
      );
      yield put(loadUserAttributesFailed());
    }
  }

  public *requestLoadAcessibleFleets() {
    try {
      const accessibleFleets: AccessibleFleet[] = yield call(
        this.userRequester.getUserAcessibleFleets
      );
      yield put(loadAccessibleFleetsSucceeded(accessibleFleets));
    } catch (err) {
      yield put(
        showAlertActionCreator(
          AlertTypes.ERROR,
          'Houve um erro, tente novamente mais tarde.'
        )
      );
      yield put(loadUserAttributesFailed());
    }
  }
}

export function* UserSagasListener() {
  const usersSagas = new UsersSagas(
    new UserRequester({ host: env.herbieApiHost })
  );
  yield all([
    takeLatest(
      UsersActionTypes.LOAD_USERS_REQUEST,
      usersSagas.requestLoadFleetUsersSaga
    ),
    takeLatest(
      UsersActionTypes.DELETE_USER_REQUEST,
      usersSagas.requestDeleteUserSaga
    ),
    takeLatest(
      UsersActionTypes.LOAD_ACCESSIBLE_FLEETS_REQUEST,
      usersSagas.requestLoadAcessibleFleets
    ),
  ]);
}
